import React, { useEffect, useState } from 'react';
import { Loader2, Play, X, Video } from 'lucide-react';
import { getAuthHeaders } from '@/hooks/useAuth';

// Shared "Choose from your library" modal for the quick tools. Lists everything
// My Library aggregates (uploads/recordings, interview takes, enhancer renders);
// picking an item stages a copy into the ephemeral tool bucket via
// POST /api/my-library/stage and hands back { key, downloadUrl, name } — the
// same shape the tools' /get-upload-url flow produces, so a library video
// slots in exactly like a fresh upload.

export interface StagedLibraryFile { key: string; downloadUrl: string; name: string; }

/** Probe duration + dimensions from a video URL (e.g. the staged presigned URL). */
export function probeVideoUrl(url: string): Promise<{ duration: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    let settled = false;
    const cleanup = () => { v.onloadedmetadata = null; v.onerror = null; v.removeAttribute('src'); v.load(); };
    // Metadata is best-effort everywhere this is called — but a stalled load
    // (e.g. a non-faststart MP4 whose moov atom sits at the end, behind a
    // proxy) fires NEITHER callback, and an unsettled promise here froze the
    // whole add-from-library flow. Time out and resolve with zeros instead.
    const timer = window.setTimeout(() => {
      if (settled) return; settled = true; cleanup();
      resolve({ duration: 0, width: 0, height: 0 });
    }, 15000);
    v.onloadedmetadata = () => {
      if (settled) return; settled = true; window.clearTimeout(timer);
      const meta = { duration: v.duration || 0, width: v.videoWidth || 0, height: v.videoHeight || 0 };
      cleanup();
      resolve(meta);
    };
    v.onerror = () => {
      if (settled) return; settled = true; window.clearTimeout(timer);
      cleanup(); reject(new Error('Could not read video metadata'));
    };
    v.src = url;
  });
}

/** Display file name for a staged pick, carrying the staged key's real extension
 * (so tools' needsConversion(fileName) checks behave like a fresh upload). */
export function stagedFileName(f: StagedLibraryFile): string {
  const ext = (f.key.split('.').pop() || 'mp4').toLowerCase();
  return f.name.toLowerCase().endsWith(`.${ext}`) ? f.name : `${f.name}.${ext}`;
}

interface PickEntry { id: string; name: string; sub: string; s3Key: string; thumbnailUrl: string | null; }

const src = (key: string) => (key.startsWith('http') || key.startsWith('/api/')) ? key : `/api/s3/${key}`;

export default function LibraryPicker({ open, onClose, onPick, stageTarget = 'ephemeral' }: {
  open: boolean;
  onClose: () => void;
  onPick: (file: StagedLibraryFile) => void;
  /** 'permanent' stages into hey-joe-videos with a proxy URL — required by the
   * Video Enhancer, whose URL resolver assumes the default proxy bucket. */
  stageTarget?: 'ephemeral' | 'permanent' | 'lesson';
}) {
  const [entries, setEntries] = useState<PickEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [stagingId, setStagingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError(null);
    fetch('/api/my-library', { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(r.status === 403 ? 'Creator tier required' : 'Could not load your library'); return r.json(); })
      .then(d => {
        const list: PickEntry[] = [];
        for (const i of d.items || []) list.push({ id: `u-${i._id}`, name: i.name, sub: 'Upload', s3Key: i.s3Key, thumbnailUrl: null });
        for (const t of d.takes || []) list.push({ id: `t-${t.interviewId}-${t.takeId}`, name: t.guestName || t.interviewTitle, sub: 'Interview', s3Key: t.s3Key, thumbnailUrl: t.thumbnailUrl });
        for (const e of d.enhancerItems || []) { if (e.s3Key) list.push({ id: e._id, name: e.name, sub: e.projectName ? `Enhancer · ${e.projectName}` : 'Enhancer', s3Key: e.s3Key, thumbnailUrl: e.thumbnailUrl }); }
        setEntries(list);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [open]);

  const pick = async (entry: PickEntry) => {
    setStagingId(entry.id); setError(null);
    try {
      const r = await fetch('/api/my-library/stage', {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3Key: entry.s3Key, name: entry.name, permanent: stageTarget === 'permanent', ...(stageTarget === 'lesson' ? { dest: 'lesson' } : {}) }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Could not use this file');
      const staged = await r.json();
      onPick({ key: staged.key, downloadUrl: staged.downloadUrl, name: entry.name });
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally { setStagingId(null); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 70, background: 'oklch(0 0 0 / 0.72)', padding: 20 }} onClick={onClose}>
      <div className="flex flex-col" style={{ width: '100%', maxWidth: 720, maxHeight: '84vh', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', borderRadius: 'var(--hj-r-md)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 flex-shrink-0" style={{ padding: '12px 16px', borderBottom: '1px solid var(--hj-hairline-soft)' }}>
          <Video className="w-4 h-4" style={{ color: 'var(--hj-ink-3)' }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)' }}>Choose from your library</span>
          <button type="button" onClick={onClose} className="inline-flex items-center justify-center flex-shrink-0" style={{ marginLeft: 'auto', width: 32, height: 32, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-2)', cursor: 'pointer' }}><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto" style={{ padding: 14 }}>
          {error && <p className="m-0" style={{ marginBottom: 10, fontSize: 13, color: 'oklch(0.65 0.2 25)' }}>{error}</p>}
          {loading ? (
            <div className="flex justify-center" style={{ padding: '40px 0' }}><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--hj-ink-3)' }} /></div>
          ) : entries.length === 0 ? (
            <p className="m-0 text-center" style={{ padding: '32px 0', fontSize: 13.5, color: 'var(--hj-ink-2)' }}>
              Your library is empty — record, upload, or interview someone at <a href="/my-library" style={{ color: 'var(--hj-ink-2)', textDecoration: 'underline' }}>My Library</a>.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {entries.map(entry => (
                <button key={entry.id} type="button" disabled={!!stagingId} onClick={() => pick(entry)} className="text-left" style={{ borderRadius: 10, border: '1px solid var(--hj-hairline)', overflow: 'hidden', background: 'var(--hj-surface-2)', padding: 0, cursor: stagingId ? 'default' : 'pointer', opacity: stagingId && stagingId !== entry.id ? 0.5 : 1 }}>
                  <div className="relative" style={{ aspectRatio: '16/9', background: '#000' }}>
                    {entry.thumbnailUrl
                      ? <img src={src(entry.thumbnailUrl)} alt={entry.name} className="w-full h-full" style={{ objectFit: 'cover' }} />
                      : <video src={src(entry.s3Key)} preload="metadata" className="w-full h-full" style={{ objectFit: 'cover', pointerEvents: 'none' }} muted />}
                    <span className="absolute inset-0 flex items-center justify-center" style={{ background: 'oklch(0 0 0 / 0.15)' }}>
                      {stagingId === entry.id ? <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#fff' }} /> : <Play className="w-5 h-5" style={{ color: '#fff', fill: '#fff', opacity: 0.85 }} />}
                    </span>
                  </div>
                  <div style={{ padding: '7px 10px' }}>
                    <div className="truncate" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-ink)' }}>{entry.name}</div>
                    <div className="truncate" style={{ fontSize: 10.5, color: 'var(--hj-ink-3)' }}>{entry.sub}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
